import { useLingui } from '@lingui/react/macro';
import { type FC, memo } from 'react';
import { useParams } from 'wouter';
import { PageMeta } from '@/base/PageMeta';
import { LandingLayout } from '@/landing/LandingLayout';
import { useCoin } from '@/shared/api';
import { useRuntimeEnv } from '@/shared/runtimeEnv';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Gate } from '../../gate';
import { CoinBreadcrumb } from './fields/CoinBreadcrumb';
import { CoinHero } from './fields/CoinHero';
import { CoinJsonLd } from './fields/CoinJsonLd';
import { CoinOrderedSections } from './fields/CoinOrderedSections';
import { CoinTradeWidget } from './fields/CoinTradeWidget';

export const CoinPage: FC<{ type: 'buy' | 'price' }> = memo(({ type }) => {
  const { slug } = useParams<{ slug: string }>();
  const normalizedSlug = slug?.toUpperCase();
  const { t } = useLingui();
  const { isStandalone } = useRuntimeEnv();
  const coin = useCoin(normalizedSlug);
  usePupeteerSignal(!!coin.data);

  const coinName = coin.data?.name;
  const pageTitle = type === 'buy' ? t`Buy ${coinName}` : t`${coinName} Price`;
  const pageDescription =
    type === 'buy'
      ? t`Buy ${coinName} (${normalizedSlug}) on IQX — Iraq's first crypto exchange.`
      : t`${coinName} (${normalizedSlug}) live price, charts and market data on IQX.`;

  const isValid = coin.isLoading || !!coin.data;

  if (!isValid) {
    throw new Error(
      `404:${t`We couldn't load market data for ${normalizedSlug.toUpperCase()}. It may be temporarily unavailable or the slug is incorrect.`}`,
    );
  }

  return (
    <LandingLayout hideFooter={isStandalone}>
      {coinName && (
        <PageMeta
          title={pageTitle}
          description={pageDescription}
          localizedPath={`/${type === 'buy' ? 'buy-and-sell' : 'price'}/${slug}`}
        />
      )}
      <CoinJsonLd />
      <main className="grid w-full grid-cols-3 gap-widget max-w-desktop mx-auto h-full pb-widget">
        <div className="col-span-3 desktop:col-span-2">
          <CoinBreadcrumb slug={normalizedSlug} type={type} />
          <CoinHero slug={normalizedSlug} type={type} />
          <CoinOrderedSections slug={normalizedSlug} type={type} />
          <Gate
            hooks={[
              Gate.hooks.hasFeature('trade'),
              Gate.hooks.isStandalone(false),
            ]}
          >
            <section className="max-w-desktop mx-auto px-3 desktop:hidden pb-8">
              <CoinTradeWidget slug={normalizedSlug} />
            </section>
          </Gate>
        </div>
        <Gate
          hooks={[
            Gate.hooks.hasFeature('trade'),
            Gate.hooks.isStandalone(false),
          ]}
        >
          <section className="hidden desktop:block desktop:col-span-1 desktop:py-28">
            <CoinTradeWidget slug={normalizedSlug} />
          </section>
        </Gate>
      </main>
    </LandingLayout>
  );
});
